import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  useToast,
  useColorModeValue,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Icon,
  Center,
  Spinner,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiActivity,
  FiTarget,
  FiMenu,
} from 'react-icons/fi';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../../../utils/init-firebase';
import { supabase } from '../../../utils/supabase';
import { useCrmSettings } from '../../../contexts/CrmSettingsContext';
import { ReactSortable } from 'react-sortablejs';

export default function ActivityTypesManager() {
  const toast = useToast();
  const { dispositions, nextActions, loading: settingsLoading, refreshDispositions } = useCrmSettings();
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemType, setItemType] = useState('disposition');
  const [saving, setSaving] = useState(false);
  
  const [localDispositions, setLocalDispositions] = useState([]);
  const [localNextActions, setLocalNextActions] = useState([]);
  const [dispositionsOrderChanged, setDispositionsOrderChanged] = useState(false);
  const [nextActionsOrderChanged, setNextActionsOrderChanged] = useState(false);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const tableHeaderBG = useColorModeValue('gray.50', 'gray.800');
  const hoverBG = useColorModeValue('gray.50', 'gray.700');
  const dragHandleBG = useColorModeValue('gray.100', 'gray.600');

  useEffect(() => {
    if (dispositions && dispositions.length > 0) {
      setLocalDispositions(
        dispositions
          .filter(d => d.value)
          .map((d, idx) => ({ ...d, id: d.id || d.value || `disp-${idx}` }))
      );
    }
  }, [dispositions]);

  useEffect(() => {
    if (nextActions && nextActions.length > 0) {
      setLocalNextActions(nextActions.map((a, idx) => ({ ...a, id: a.value || `empty-${idx}` })));
    }
  }, [nextActions]);

  const handleAddDisposition = () => {
    setSelectedItem(null);
    setItemType('disposition');
    onOpen();
  };

  const handleAddNextAction = () => {
    setSelectedItem(null);
    setItemType('nextAction');
    onOpen();
  };

  const handleEdit = (item, type) => {
    setSelectedItem(item);
    setItemType(type);
    onOpen();
  };

  const handleDelete = async (item, type) => {
    if (!window.confirm(`Are you sure you want to delete this ${type}?`)) return;

    setSaving(true);
    try {
      if (type === 'disposition') {
        const { error } = await supabase
          .from('crm_dispositions')
          .delete()
          .eq('value', item.value);
        if (error) throw error;
        if (refreshDispositions) refreshDispositions();
      } else {
        const functions = getFunctions(app, 'us-east1');
        const updateCrmSettings = httpsCallable(functions, 'updateCrmSettings');
        const updatedItems = nextActions.filter(i => i.value !== item.value);
        await updateCrmSettings({ updates: { nextActions: updatedItems } });
      }
      toast({
        title: `${type === 'disposition' ? 'Disposition' : 'Next Action'} deleted`,
        status: 'success',
        duration: 2000,
      });
    } catch (error) {
      console.error('Error deleting:', error);
      toast({ title: 'Error', description: error.message, status: 'error', duration: 5000 });
    } finally {
      setSaving(false);
    }
  };

  const handleReorderDispositions = (newList) => {
    setLocalDispositions(newList);
    const originalOrder = dispositions.filter(d => d.value).map(d => d.value).join(',');
    const newOrder = newList.map(d => d.value).join(',');
    setDispositionsOrderChanged(originalOrder !== newOrder);
  };

  const handleReorderNextActions = (newList) => {
    setLocalNextActions(newList);
    const originalOrder = nextActions.map(a => a.value).join(',');
    const newOrder = newList.map(a => a.value).join(',');
    setNextActionsOrderChanged(originalOrder !== newOrder);
  };

  const saveOrder = async (type) => {
    setSaving(true);
    try {
      if (type === 'disposition') {
        for (let i = 0; i < localDispositions.length; i++) {
          const d = localDispositions[i];
          if (!d.value) continue;
          await supabase
            .from('crm_dispositions')
            .update({ sort_order: i + 1 })
            .eq('value', d.value);
        }
        if (refreshDispositions) refreshDispositions();
      } else {
        const functions = getFunctions(app, 'us-east1');
        const updateCrmSettings = httpsCallable(functions, 'updateCrmSettings');
        const itemsToSave = localNextActions.map(({ id, ...rest }) => rest);
        await updateCrmSettings({ updates: { nextActions: itemsToSave } });
      }
      toast({
        title: 'Order saved',
        description: `${type === 'disposition' ? 'Disposition' : 'Next action'} order updated`,
        status: 'success',
        duration: 2000,
      });
      if (type === 'disposition') setDispositionsOrderChanged(false);
      else setNextActionsOrderChanged(false);
    } catch (error) {
      console.error('Error saving order:', error);
      toast({ title: 'Error', description: error.message, status: 'error', duration: 5000 });
    } finally {
      setSaving(false);
    }
  };

  if (settingsLoading) {
    return <Center py={12}><Spinner size="lg" /></Center>;
  }

  const activeDispositions = localDispositions.filter(d => d.value);

  return (
    <Box>
      <VStack align="stretch" spacing={6}>
        <HStack justify="space-between">
          <VStack align="flex-start" spacing={0}>
            <Heading size="md">Activity Types</Heading>
            <Text fontSize="sm" color="gray.600">
              Customize dispositions and next actions for deal interactions
            </Text>
          </VStack>
        </HStack>

        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">
            Drag items to reorder them. The order will be reflected in dropdown menus throughout the CRM.
          </Text>
        </Alert>

        <Tabs colorScheme="blue">
          <TabList>
            <Tab>
              <HStack spacing={2}>
                <Icon as={FiActivity} />
                <Text>Dispositions</Text>
                <Badge>{activeDispositions.length}</Badge>
              </HStack>
            </Tab>
            <Tab>
              <HStack spacing={2}>
                <Icon as={FiTarget} />
                <Text>Next Actions</Text>
                <Badge>{nextActions?.length || 0}</Badge>
              </HStack>
            </Tab>
          </TabList>

          <TabPanels>
            <TabPanel px={0} pt={4}>
              <VStack align="stretch" spacing={4}>
                <HStack justify="space-between">
                  {dispositionsOrderChanged && (
                    <Button colorScheme="green" size="sm" onClick={() => saveOrder('disposition')}
                      isLoading={saving} loadingText="Saving...">
                      Save Order
                    </Button>
                  )}
                  <Box flex={1} />
                  <Button leftIcon={<FiPlus />} colorScheme="blue" size="sm" onClick={handleAddDisposition}>
                    Add Disposition
                  </Button>
                </HStack>
                
                <Box overflowX="auto">
                  <Table variant="simple" size="sm">
                    <Thead bg={tableHeaderBG}>
                      <Tr>
                        <Th w="40px"></Th>
                        <Th>Value</Th>
                        <Th>Label</Th>
                        <Th>Color</Th>
                        <Th>Actions</Th>
                      </Tr>
                    </Thead>
                    <ReactSortable
                      tag={Tbody}
                      list={activeDispositions}
                      setList={handleReorderDispositions}
                      animation={200}
                      handle=".drag-handle"
                    >
                      {activeDispositions.map((item) => (
                        <Tr key={item.id} _hover={{ bg: hoverBG }}>
                          <Td p={1}>
                            <IconButton icon={<FiMenu />} size="xs" variant="ghost" aria-label="Drag"
                              className="drag-handle" cursor="grab" _hover={{ bg: dragHandleBG }} />
                          </Td>
                          <Td fontFamily="mono" fontSize="xs">{item.value}</Td>
                          <Td fontWeight="medium">{item.label}</Td>
                          <Td><Badge colorScheme={item.color || 'gray'} fontSize="xs">{item.color || 'gray'}</Badge></Td>
                          <Td>
                            <HStack spacing={1}>
                              <IconButton icon={<FiEdit2 />} size="xs" variant="ghost" aria-label="Edit"
                                onClick={() => handleEdit(item, 'disposition')} />
                              <IconButton icon={<FiTrash2 />} size="xs" variant="ghost" colorScheme="red"
                                aria-label="Delete" onClick={() => handleDelete(item, 'disposition')} />
                            </HStack>
                          </Td>
                        </Tr>
                      ))}
                    </ReactSortable>
                  </Table>
                </Box>
              </VStack>
            </TabPanel>

            <TabPanel px={0} pt={4}>
              <VStack align="stretch" spacing={4}>
                <HStack justify="space-between">
                  {nextActionsOrderChanged && (
                    <Button colorScheme="green" size="sm" onClick={() => saveOrder('nextAction')}
                      isLoading={saving} loadingText="Saving...">
                      Save Order
                    </Button>
                  )}
                  <Box flex={1} />
                  <Button leftIcon={<FiPlus />} colorScheme="blue" size="sm" onClick={handleAddNextAction}>
                    Add Next Action
                  </Button>
                </HStack>

                <Box overflowX="auto">
                  <Table variant="simple" size="sm">
                    <Thead bg={tableHeaderBG}>
                      <Tr>
                        <Th w="40px"></Th>
                        <Th>Value</Th>
                        <Th>Label</Th>
                        <Th>Actions</Th>
                      </Tr>
                    </Thead>
                    <ReactSortable
                      tag={Tbody}
                      list={localNextActions}
                      setList={handleReorderNextActions}
                      animation={200}
                      handle=".drag-handle"
                    >
                      {localNextActions.map((item) => (
                        <Tr key={item.id} _hover={{ bg: hoverBG }}>
                          <Td p={1}>
                            <IconButton icon={<FiMenu />} size="xs" variant="ghost" aria-label="Drag"
                              className="drag-handle" cursor="grab" _hover={{ bg: dragHandleBG }} />
                          </Td>
                          <Td fontFamily="mono" fontSize="xs">{item.value || '(empty)'}</Td>
                          <Td fontWeight="medium">{item.label}</Td>
                          <Td>
                            <HStack spacing={1}>
                              <IconButton icon={<FiEdit2 />} size="xs" variant="ghost" aria-label="Edit"
                                onClick={() => handleEdit(item, 'nextAction')} />
                              <IconButton icon={<FiTrash2 />} size="xs" variant="ghost" colorScheme="red"
                                aria-label="Delete" onClick={() => handleDelete(item, 'nextAction')}
                                isDisabled={item.value === ''} />
                            </HStack>
                          </Td>
                        </Tr>
                      ))}
                    </ReactSortable>
                  </Table>
                </Box>
              </VStack>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>

      <EditorModal
        isOpen={isOpen}
        onClose={onClose}
        item={selectedItem}
        itemType={itemType}
        existingItems={itemType === 'disposition' ? dispositions : nextActions}
        refreshDispositions={refreshDispositions}
      />
    </Box>
  );
}

function EditorModal({ isOpen, onClose, item, itemType, existingItems, refreshDispositions }) {
  const toast = useToast();
  const [value, setValue] = useState('');
  const [label, setLabel] = useState('');
  const [color, setColor] = useState('gray');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (item) {
      setValue(item.value || '');
      setLabel(item.label || '');
      setColor(item.color || 'gray');
    } else {
      setValue('');
      setLabel('');
      setColor('gray');
    }
  }, [item, isOpen]);

  const handleSave = async () => {
    if (!label.trim()) {
      toast({ title: 'Missing label', description: 'Label is required', status: 'warning', duration: 3000 });
      return;
    }

    if (value && !/^[a-z0-9-]+$/.test(value)) {
      toast({
        title: 'Invalid value format',
        description: 'Value must be lowercase alphanumeric with hyphens only',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setIsSaving(true);
    try {
      if (itemType === 'disposition') {
        if (item) {
          const { error } = await supabase
            .from('crm_dispositions')
            .update({ label: label.trim(), color: color.trim() || 'gray' })
            .eq('value', item.value);
          if (error) throw error;
        } else {
          if (existingItems.some(i => i.value === value.trim())) {
            toast({ title: 'Duplicate value', description: 'A disposition with this value already exists', status: 'error', duration: 3000 });
            setIsSaving(false);
            return;
          }
          const maxSort = Math.max(0, ...(existingItems || []).map(d => d.sortOrder || 0));
          const { error } = await supabase
            .from('crm_dispositions')
            .insert({ value: value.trim(), label: label.trim(), color: color.trim() || 'gray', sort_order: maxSort + 1 });
          if (error) throw error;
        }
        if (refreshDispositions) refreshDispositions();
      } else {
        const functions = getFunctions(app, 'us-east1');
        const updateCrmSettings = httpsCallable(functions, 'updateCrmSettings');
        let updatedItems = [...(existingItems || [])];
        const newItem = { value: value.trim(), label: label.trim() };

        if (item) {
          const index = updatedItems.findIndex(i => i.value === item.value);
          if (index > -1) updatedItems[index] = newItem;
        } else {
          if (updatedItems.some(i => i.value === value.trim())) {
            toast({ title: 'Duplicate value', description: 'A next action with this value already exists', status: 'error', duration: 3000 });
            setIsSaving(false);
            return;
          }
          updatedItems.push(newItem);
        }
        await updateCrmSettings({ updates: { nextActions: updatedItems } });
      }

      toast({
        title: item ? 'Updated' : 'Created',
        description: `${itemType === 'disposition' ? 'Disposition' : 'Next action'} ${item ? 'updated' : 'created'} successfully`,
        status: 'success',
        duration: 2000,
      });
      onClose();
    } catch (error) {
      console.error('Error saving:', error);
      toast({ title: 'Error', description: error.message, status: 'error', duration: 5000 });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {item ? 'Edit' : 'Create'} {itemType === 'disposition' ? 'Disposition' : 'Next Action'}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel fontSize="sm">Value (Unique ID)</FormLabel>
              <Input
                value={value}
                onChange={(e) => setValue(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                placeholder="e.g., spoke-qualified"
                isDisabled={!!item}
              />
              <Text fontSize="xs" color="gray.600" mt={1}>Lowercase, hyphens only.</Text>
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Display Label</FormLabel>
              <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g., Spoke - Qualified" />
            </FormControl>

            {itemType === 'disposition' && (
              <FormControl>
                <FormLabel fontSize="sm">Color</FormLabel>
                <Input value={color} onChange={(e) => setColor(e.target.value)} placeholder="e.g., green, blue, red" />
                <Text fontSize="xs" color="gray.600" mt={1}>Chakra color name (blue, green, red, orange, teal, purple, cyan, gray)</Text>
              </FormControl>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
          <Button colorScheme="blue" onClick={handleSave} isLoading={isSaving} loadingText="Saving...">
            {item ? 'Update' : 'Create'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
